import { useCallback, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { removeMessageListener } from '@dvag/dfs-orchestrator-client';

import {
  navigate as orchestratorNavigate,
  setMicroappIsDirty,
} from '@dvag/dfs-orchestrator-client/messengers';
import { addCustomMessageListener } from '@dvag/dfs-orchestrator-client/listeners';

type Continuation = { onConfirm: () => void; onCancel: () => void } | undefined;
export type ContinutationRequest = () => void;
export type OnCanContinue = (a: boolean) => void;

type CanContinue = { id: string; value: boolean };

export const useContinuation = () => {
  const [continuation, setContinuation] = useState<Continuation>(undefined);
  const [canContinueList, setCanContinueList] = useState<CanContinue[]>([]);

  const canContinue = canContinueList.every((item) => item.value);

  const [continuationRequestList, setContinuationRequestList] = useState<ContinutationRequest[]>(
    [],
  );

  const onContinuationRequest = useCallback((continuationRequestCallback: ContinutationRequest) => {
    setContinuationRequestList((previous) => [...previous, continuationRequestCallback]);
  }, []);

  const getContinuationHandler = useCallback(
    (continuationCallback: () => void) => () => {
      const confirmationTimeout = 600;
      const timeout = 100;

      continuationRequestList.forEach((request) => request());
      if (canContinue) {
        continuationCallback();
      } else {
        setTimeout(
          () =>
            setContinuation({
              onConfirm: () => {
                setContinuation(undefined);
                setMicroappIsDirty(false);
                setTimeout(() => continuationCallback(), confirmationTimeout);
              },
              onCancel: () => setContinuation(undefined),
            }),
          timeout,
        );
      }
    },
    [canContinue, continuationRequestList],
  );

  const getCanContinueHandler = useCallback(() => {
    const id = uuidv4();
    return (canContinueCond: boolean) => {
      setCanContinueList((prev) => {
        const index = prev.findIndex((el) => el.id === id);
        if (index === -1) {
          return [...prev, { id, value: canContinueCond }];
        }
        const newCanContinue = [...prev];
        newCanContinue[index] = { id, value: canContinueCond };
        return newCanContinue;
      });
    };
  }, []);

  useEffect(() => {
    setMicroappIsDirty(!canContinue);
    if (continuation !== undefined && canContinue) {
      continuation.onConfirm();
    }
  }, [canContinue, continuation]);

  useEffect(() => {
    const customMsgId = addCustomMessageListener('navigationRequest', (data) => {
      const requestPayload = data.payload;
      const { url, nextUrl } = requestPayload;
      getContinuationHandler(() => orchestratorNavigate(url, nextUrl))();
    });
    return () => {
      removeMessageListener(customMsgId);
    };
  }, [getContinuationHandler]);

  return {
    continuation,
    getContinuationHandler,
    getCanContinueHandler,
    onContinuationRequest,
  };
};
